import { type RequirementIdentifier, useIntl } from '@capsicum/common';
import {
	type InitFlowPageProps,
	NCOnboardingAnalyticsEvents,
	NCOnboardingRoutesEnum,
	useNCWOnboardingRoutes,
	useResetMainScreen
} from '@capsicum/features/nc-onboarding/shared';
import { Requirement } from '@capsicum/features/regulations/web';
import { match } from 'ts-pattern';

import { CongratulationsScreen } from '../../../ui';
import { CreateYourWalletScreen } from '../../../ui/create-your-wallet';
import { DeleteAccountScreen } from '../../../ui/delete-account';
import { NcOnboarding } from '../../../ui/onboarding';
import { SupportScreen } from '../../../ui/support';
import { VerificationScreen } from '../../../ui/verify/VerificationScreen';
import { NCRegistration } from '../../registration';

const createRequirementComponent = (requirementIdentifier: RequirementIdentifier) => {
	const Component = ({ onSubmit }: { onSubmit: () => void }) => (
		<Requirement isUniqueRequirement requirementIdentifier={requirementIdentifier} onDismiss={onSubmit} />
	);

	Component.displayName = `RequirementComponent_${requirementIdentifier}`;

	return Component;
};

export const NCWExistingUserOnboardingFlow = ({ done }: InitFlowPageProps) => {
	const { currentRoute, goToPage, onDelete, onDone, triggerAnalyticsEvent } = useNCWOnboardingRoutes({ done });
	const { $t } = useIntl();

	useResetMainScreen();

	const ProofOfIdentity = createRequirementComponent('proof_of_id');

	const ProofOfAdress = createRequirementComponent('proof_of_address');

	return match(currentRoute)
		.with(NCOnboardingRoutesEnum.ONBOARDING_SLIDES, () => (
			<NcOnboarding
				onDone={() => {
					triggerAnalyticsEvent(NCOnboardingAnalyticsEvents.GO_TO_CREATE_WALLET_SCREEN);
					goToPage(NCOnboardingRoutesEnum.CREATE_YOUR_WALLET);
				}}
			/>
		))
		.with(NCOnboardingRoutesEnum.CREATE_YOUR_WALLET, () => (
			<CreateYourWalletScreen
				onDone={() => {
					triggerAnalyticsEvent(NCOnboardingAnalyticsEvents.GO_TO_REGISTER_PAGE);
					goToPage(NCOnboardingRoutesEnum.REGISTRATION);
				}}
				onTellMeMore={() => {
					triggerAnalyticsEvent(NCOnboardingAnalyticsEvents.GO_TO_SUPPORT_PAGE);
					goToPage(NCOnboardingRoutesEnum.SUPPORT);
				}}
			/>
		))
		.with(NCOnboardingRoutesEnum.SUPPORT, () => (
			<SupportScreen
				onBack={() => {
					goToPage(NCOnboardingRoutesEnum.CREATE_YOUR_WALLET);
				}}
				onDelete={() => {
					triggerAnalyticsEvent(NCOnboardingAnalyticsEvents.GO_TO_DELETE_ACCOUNT);
					goToPage(NCOnboardingRoutesEnum.DELETE_ACCOUNT);
				}}
			/>
		))
		.with(NCOnboardingRoutesEnum.DELETE_ACCOUNT, () => (
			<DeleteAccountScreen
				onBack={() => goToPage(NCOnboardingRoutesEnum.SUPPORT)}
				onSuccess={() => {
					triggerAnalyticsEvent(NCOnboardingAnalyticsEvents.USER_DELETED);
					onDelete();
				}}
			/>
		))
		.with(NCOnboardingRoutesEnum.REGISTRATION, () => (
			<NCRegistration
				variant="INIT_FLOW"
				waitForWalletStatus={false}
				onSuccess={() => goToPage(NCOnboardingRoutesEnum.CONGRATULATIONS)}
			/>
		))
		.with(NCOnboardingRoutesEnum.CONGRATULATIONS, () => <CongratulationsScreen onDone={onDone} />)
		.with(NCOnboardingRoutesEnum.IDENTITY_VERIFICATION, () => (
			<VerificationScreen
				showBackButton
				testID="identity-verification-top-bar"
				title={$t('kyc.level2Text')}
				VerificationStep={ProofOfIdentity}
			/>
		))
		.with(NCOnboardingRoutesEnum.ADDRESS_VERIFICATION, () => (
			<VerificationScreen
				testID="postal-address-verification-top-bar"
				title={$t('kyc.level3Text')}
				VerificationStep={ProofOfAdress}
			/>
		))
		.otherwise(() => <NcOnboarding onDone={() => goToPage(NCOnboardingRoutesEnum.CREATE_YOUR_WALLET)} />);
};
